import {DashboardRepository} from '../../domain/dashboard/repositories/DashboardRepository';
import {DashboardData, RecentScan, DeliveryItem} from '../../domain/dashboard/entities/DashboardData';
import {seliApiClient, ApiError} from '../../shared/http/apiClient';
import {AsyncStorageAdapter} from '../storage/AsyncStorageAdapter';

interface DeliveriesApiResponse {
  items: DeliveryItem[];
  total: number;
}

export class DashboardRepositoryImpl implements DashboardRepository {
  private storageAdapter: AsyncStorageAdapter;

  constructor() {
    this.storageAdapter = new AsyncStorageAdapter();
  }

  async getDashboardData(): Promise<DashboardData> {
    try {
      // Get auth token
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Fetch deliveries from API (created by current user)
      const deliveries = await this.getDeliveriesByCreatedUser();

      // Calculate stats
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // Count deliveries today
      const totalScansToday = deliveries.filter(delivery => {
        const deliveryDate = new Date(delivery.created_at);
        return deliveryDate >= today;
      }).length;

      // Count deliveries this month
      const scansThisMonth = deliveries.filter(delivery => {
        const deliveryDate = new Date(delivery.created_at);
        return deliveryDate >= firstDayOfMonth;
      }).length;

      // Sort by date (most recent first)
      const sortedDeliveries = [...deliveries].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      return {
        scansThisMonth,
        totalScansToday,
        recentScans: [], // Empty for now, can be populated with redemptions if needed
        deliveries: sortedDeliveries,
      };
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error fetching dashboard data:', apiError);
      
      // Return empty data on error instead of throwing
      return {
        scansThisMonth: 0,
        totalScansToday: 0,
        recentScans: [],
        deliveries: [],
      };
    }
  }

  async getDeliveriesByCreatedUser(): Promise<DeliveryItem[]> {
    try {
      // Get auth token
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Fetch deliveries from API (returns paginated response)
      const response = await seliApiClient.get<DeliveriesApiResponse>(
        `/api/benefits_managment/deliveries/by-created-by/${user.id}`,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      // Extract items from paginated response - keep original structure
      const deliveries = response.data.items;

      return deliveries;
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error fetching deliveries:', apiError);
      
      // Return empty array on error
      return [];
    }
  }
}

